import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Chart as ChartJS,
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

import { Diagnostics } from "../../../../../services";
import { POD_COLORS } from "../constants";
import { formatTime, toMegabytes } from "../utils/format";

import { EmptyState } from "./Panel";

ChartJS.register(
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	Filler,
);

const MemoryChart: React.FC<MemoryChart.Props> = ({ pods, calls }) => {
	const { t } = useTranslation();

	const hasData = pods.some((pod) => pod.points.length > 0);

	const data = useMemo(() => {
		const memoryDatasets = pods.map((pod, index) => ({
			label: `heap · ${pod.pod.slice(-12)}`,
			yAxisID: "memory",
			borderColor: POD_COLORS[index % POD_COLORS.length],
			backgroundColor: POD_COLORS[index % POD_COLORS.length],
			borderWidth: 2,
			pointRadius: 0,
			tension: 0.25,
			data: pod.points.map((point) => ({
				x: point.ts,
				y: toMegabytes(point.heapUsed),
			})),
		}));

		/**
		 * The call series goes on a secondary axis: it matters not on its own
		 * but as a way to tell whether memory grows together with the load or
		 * independently of it.
		 */
		const callsDataset = calls.length
			? [
					{
						label: t(
							"settings.systemPerformance.operations.calls",
						) as string,
						yAxisID: "calls",
						borderColor: "#B5B5B5",
						backgroundColor: "rgba(181, 181, 181, 0.25)",
						borderWidth: 1,
						pointRadius: 0,
						fill: true,
						tension: 0.25,
						data: calls.map((point) => ({
							x: point.ts,
							y: point.calls,
						})),
					},
			  ]
			: [];

		return { datasets: [...callsDataset, ...memoryDatasets] };
	}, [calls, pods, t]);

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			animation: false as const,
			interaction: { mode: "nearest" as const, intersect: false },
			scales: {
				x: {
					type: "linear" as const,
					ticks: {
						maxTicksLimit: 10,
						callback: (value: number | string) =>
							formatTime(Number(value)),
					},
				},
				memory: {
					type: "linear" as const,
					position: "left" as const,
					title: { display: true, text: "MB" },
					beginAtZero: true,
				},
				calls: {
					type: "linear" as const,
					position: "right" as const,
					title: {
						display: true,
						text: t(
							"settings.systemPerformance.operations.calls",
						) as string,
					},
					beginAtZero: true,
					grid: { drawOnChartArea: false },
				},
			},
			plugins: {
				// The datalabels plugin is registered globally by the reports
				// page and turns a dense time series into noise.
				datalabels: { display: false },
				legend: {
					position: "top" as const,
					labels: { boxWidth: 12, boxHeight: 12 },
				},
				tooltip: {
					callbacks: {
						title: (items: any[]) =>
							formatTime(items?.[0]?.parsed?.x),
					},
				},
			},
		}),
		[t],
	);

	if (!hasData) {
		return (
			<EmptyState>
				{t("settings.systemPerformance.overview.empty")}
			</EmptyState>
		);
	}

	return (
		<div style={{ height: 280, width: "100%", maxWidth: "100%", minWidth: 0 }}>
			<Line options={options as any} data={data as any} />
		</div>
	);
};

declare namespace MemoryChart {
	interface Props {
		pods: Diagnostics.TimelineResponse["pods"];
		calls: Diagnostics.TimelineResponse["calls"];
	}
}

export default MemoryChart;
